import React, { useState } from 'react';
import { Dropdown, Button } from 'antd';
import { DownOutlined, GlobalOutlined } from '@ant-design/icons';
import { languages } from '../../constants/languages';
import { getCountryFlagByLanguage } from '../../utils/languageHelpers';

interface LanguageSelectorProps {
  className?: string;
  value: string;
  onChange: (language: string) => void;
}

interface FlagData {
  name: string;
  code: string;
  emoji: string;
  image: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className = '', value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleLanguageSelect = (languageValue: string) => {
    onChange(languageValue);
    setIsOpen(false);
  };

  const getCurrentFlag = (): FlagData | null => {
    const flagData = getCountryFlagByLanguage(value) as FlagData;
    return flagData || null;
  };

  const getNativeLanguageLabel = (languageValue: string) => {
    const languageObj = languages.find((lang) => lang.value === languageValue);
    return (languageObj as any)?.nativeLabel || languageObj?.label || 'English';
  };

  const filteredLanguages = languages.filter((lang) => {
    const label = lang.label?.toLowerCase() || '';
    const nativeLabel = (lang as any).nativeLabel?.toLowerCase() || '';
    const value = lang.value?.toLowerCase() || '';
    return (
      label.includes(searchQuery.toLowerCase()) ||
      nativeLabel.includes(searchQuery.toLowerCase()) ||
      value.includes(searchQuery.toLowerCase())
    );
  });

  const menuItems = [
    {
      key: 'search-input',
      label: (
        <div className='px-3 py-2 border-b border-gray-200 bg-white sticky top-0 z-10'>
          <input
            type='text'
            placeholder='Search language...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ),
      disabled: true
    },
    ...filteredLanguages.map((language) => {
      const flagData = getCountryFlagByLanguage(language.value) as FlagData;
      return {
        key: language.value,
        label: (
          <div className='flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded transition-colors'>
            {flagData && flagData.image && (
              <img
                src={flagData.image}
                alt={flagData.name}
                className='w-5 h-5 rounded-sm object-cover flex-shrink-0'
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const nextSibling = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextSibling) {
                    nextSibling.style.display = 'inline';
                  }
                }}
              />
            )}
            <span className='text-sm font-medium text-gray-700 truncate'>{language.label}</span>
          </div>
        ),
        onClick: () => handleLanguageSelect(language.value)
      };
    })
  ];

  const currentFlag = getCurrentFlag();

  return (
    <div className={`language-selector ${className}`}>
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        open={isOpen}
        onOpenChange={setIsOpen}
        placement='bottomRight'
        overlayClassName='language-dropdown'
        popupRender={(menu) => <div style={{ width: '280px' }}>{menu}</div>}>
        <Button
          type='text'
          className='flex items-center gap-2 px-3 py-2 h-auto hover:bg-gray-50 rounded-lg transition-colors'
          style={{ border: 'none', boxShadow: 'none' }}>
          {currentFlag && currentFlag.image ? (
            <img
              src={currentFlag.image}
              alt={currentFlag.name}
              className='w-6 h-6 rounded-sm object-cover'
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const nextSibling = e.currentTarget.nextElementSibling as HTMLElement;
                if (nextSibling) {
                  nextSibling.style.display = 'inline';
                }
              }}
            />
          ) : (
            <GlobalOutlined className='text-gray-500' />
          )}
          <span className='text-sm font-medium text-gray-700 hidden sm:inline'>
            {getNativeLanguageLabel(value)}
          </span>
          <DownOutlined className='text-xs text-gray-500 ml-1' />
        </Button>
      </Dropdown>
    </div>
  );
};

export default LanguageSelector;
